/**
 * The two documents a shareholder can take away about their own shares.
 *
 *   kvittering        money they paid in, and what it bought — aksjeloven's
 *                     receipt, asked for on 2 Sep 2026
 *   aksjeeierbevis    that they hold those shares, once the increase that
 *                     created them is registered — asked for the same day
 *
 * They live in one file because they must not be able to disagree about *which
 * shares they are describing*. `shareTransactions` is the single answer to that
 * question and both documents are built from it; two enumerations of the same
 * purchases would drift the first time either was edited, and a receipt and a
 * certificate naming different share counts for one round is the specific
 * failure worth this file's existence.
 *
 * ## What separates them
 *
 * **Neither exists until the money has arrived.** Bjarte, 2 Sep 2026: *unpaid
 * purchases must not get certificate or receipt.* So `paidDate` is the floor
 * under both, and the certificate adds one condition on top — the round having
 * been reported to Foretaksregisteret, because a capital increase takes effect
 * on registration.
 *
 * That makes the certificate a strict subset of the receipt: a holder gets the
 * receipt the day the money lands and the certificate when the increase is
 * registered, which may be weeks later. Never the other way round.
 *
 * The register itself takes a looser view — `paidAt` "records receipt, not
 * entitlement", and a purchase counts towards the holding and the percentages
 * from the day it is recorded. These documents deliberately do not follow it
 * that far. A screen showing a provisional position is one thing; a signed
 * certificate asserting title to shares nobody has paid for is another.
 *
 * ## Two transactions, one pair of documents
 *
 * The register holds exactly two things carrying a date the money arrived, and
 * both are a payment of share capital **to the company**:
 *
 *   SharePurchase.paidAt   shares subscribed in a round, paid for
 *   OptionGrant.paidDate   an exercised grant whose strike price was paid
 *
 * Both are the same event to a tax authority: shares came into existence, and
 * the holder paid a price for them on a day. Confirmed by Bjarte on 2 Sep —
 * *shares obtained through options are also paid for and need a receipt*.
 *
 * A `ShareTransfer` is deliberately **not** here. The company is not a party to
 * it and receives nothing, so there is no receipt to give; and no capital
 * increase happens, so there is nothing to report to Foretaksregisteret and no
 * certificate gated on one.
 *
 * ## What the receipt is for, which decides what it must carry
 *
 * The holder's *inngangsverdi*: what the shares cost them, for the day they
 * eventually sell. That is tegningskurs × antall aksjer, on a date — so the
 * price and the payment date are the two fields it exists to state, and the two
 * it refuses to guess at.
 *
 * ## Both state gaps rather than filling them
 *
 * The same rule as the board minutes: a document that invents an organisation
 * number or a par value is worse than one that shows a gap, because it looks
 * like a record. `missing` is that list, and both documents print it.
 */
import type { CompanyDetails } from './company'
import { effectivePrice, type Issuance, type Shareholder } from './ownership'

/**
 * The register models no share classes, which is the same as having one.
 *
 * A Norwegian AS has a single class unless its vedtekter say otherwise, and
 * nothing in this codebase has ever recorded a second one — not the issuances,
 * not the purchases, not the minutes. So the document states the class rather
 * than leaving a legally required field blank, and the day Mountainsnow issues
 * a B share this constant is where that stops being true.
 */
export const SHARE_CLASS: Record<DocLang, string> = {
  nb: 'Ordinære aksjer',
  en: 'Ordinary shares',
}

/**
 * Which language a document is being read in.
 *
 * Both open in Norwegian and offer English — Bjarte, 3 Sep 2026. Norwegian is
 * the default because that is the language these are filed in here; English
 * exists because a shareholder abroad has to be able to read what they are
 * signing off on, and the register has holders in more than one country.
 *
 * This is the *document's* language and has nothing to do with the interface,
 * which stays English throughout. The buttons around the sheet, and the ones
 * that open it, are UI.
 */
export type DocLang = 'nb' | 'en'

export type ReceiptKind = 'purchase' | 'exercise'

/**
 * A required fact nothing on file could answer.
 *
 * Keys rather than sentences, because the documents print in two languages and
 * a model that hands the view finished Norwegian prose can only ever produce
 * one of them. The view owns the words; this owns which words are needed.
 */
export type MissingField =
  | 'companyName'
  | 'companyOrgNumber'
  | 'parValue'
  | 'holderAddress'
  | 'holderId'
  | 'price'
  | 'paidDate'
  | 'registered'

/** One acquisition of newly issued shares, read off the holder's own record. */
export interface PaidTransaction {
  kind: ReceiptKind
  /** Stable within one holder — a React key, and the receipt's own reference. */
  key: string
  /** `Issuance.id` the shares were issued under. Empty when an exercise names none. */
  issuanceId: string
  shares: number
  /** What this holder paid per share. Null when nothing on file says. */
  pricePerShare: number | null
  /** ISO. The day the money landed — the date the receipt is *for*. */
  paidDate: string
  /** ISO. Subscribed or granted, which is not the day it was paid. */
  subscribedDate: string
  /** The board or general-meeting resolution behind an exercise. Empty otherwise. */
  resolution: string
  note: string
}

/**
 * Every share this holder acquired from the company, oldest first.
 *
 * The one answer to "which shares are we describing", from which both documents
 * are filtered. Reads the holder's own record and nothing else, because that is
 * all a shareholder can read about themselves. The issuances are passed in only
 * to resolve a price the purchase did not carry — `effectivePrice` is the one
 * function that decides that, here as everywhere else.
 */
export function shareTransactions(
  /**
   * Both arrays are optional, and that is not defensive noise. The shareholder
   * screen casts a Firestore document straight to `Shareholder` rather than
   * mapping it through the repository, so a holder who has never been in a
   * round has no `purchases` **key at all** — and one whose shares all arrived
   * by transfer is exactly that person. Requiring the arrays here would have
   * crashed their screen on the day they signed in.
   */
  holder: { purchases?: Shareholder['purchases']; options?: Shareholder['options'] },
  issuances: Issuance[],
): PaidTransaction[] {
  const round = (id: string) => issuances.find((i) => i.id === id)

  const purchases = (holder.purchases ?? [])
    .map((p, i): PaidTransaction | null =>
      p.paidAt
        ? {
            kind: 'purchase',
            key: `p-${p.issuanceId}-${i}`,
            issuanceId: p.issuanceId,
            shares: p.shares,
            pricePerShare: effectivePrice(p, round(p.issuanceId)),
            paidDate: p.paidAt,
            subscribedDate: p.date ?? '',
            resolution: '',
            note: p.note ?? '',
          }
        : null,
    )
    .filter((p): p is PaidTransaction => p !== null)

  /**
   * Every `exercised` grant, paid or not. Enumerating the unpaid ones and then
   * filtering them out keeps one walk of the record rather than two — and the
   * unpaid ones fall out of both documents at the same `paidDate` test that
   * excludes an unpaid purchase, which is the point: one rule, one place.
   */
  const exercises = (holder.options ?? [])
    .map((o, i): PaidTransaction | null =>
      o.status === 'exercised'
        ? {
            kind: 'exercise',
            key: `o-${o.grantDate}-${i}`,
            issuanceId: o.exerciseIssuanceId,
            shares: o.count,
            // The strike is the price, and it is on the grant. The round's own
            // price is what other subscribers paid, not what this holder did.
            pricePerShare: o.strikePrice,
            paidDate: o.paidDate,
            subscribedDate: o.exerciseDate || o.grantDate,
            resolution: o.exerciseResolution,
            note: '',
          }
        : null,
    )
    .filter((o): o is PaidTransaction => o !== null)

  return [...purchases, ...exercises].sort((a, b) => a.paidDate.localeCompare(b.paidDate))
}

/**
 * The ones with a receipt: money actually reached the company.
 *
 * Keys are unchanged from before the certificate existed, so a row that could
 * find its receipt still can.
 */
export function paidTransactions(
  holder: { purchases?: Shareholder['purchases']; options?: Shareholder['options'] },
  issuances: Issuance[],
): PaidTransaction[] {
  return shareTransactions(holder, issuances).filter((t) => t.paidDate)
}

/**
 * The ones with an aksjeeierbevis: paid for, **and** in a round that has been
 * reported to Foretaksregisteret.
 *
 * Built on `paidTransactions` rather than beside it, so the subset relationship
 * is the code and not a comment: nothing can ever be certifiable without being
 * receiptable first.
 */
export function certifiableTransactions(
  holder: { purchases?: Shareholder['purchases']; options?: Shareholder['options'] },
  issuances: Issuance[],
): PaidTransaction[] {
  return paidTransactions(holder, issuances).filter((t) =>
    Boolean(issuances.find((i) => i.id === t.issuanceId)?.registeredAt),
  )
}

/** The document, resolved to the strings and numbers a view only has to print. */
export interface Receipt {
  kind: ReceiptKind
  /** Legal name and organisation number of the company that received the money. */
  companyName: string
  companyOrgNumber: string
  holderName: string
  holderAddress: string
  /**
   * Which number `holderIdNumber` is — organisasjonsnummer for a company,
   * fødselsnummer for a person. A flag and not a label, because the label is
   * two different words in the two languages the document prints in.
   */
  holderIsCompany: boolean
  /**
   * Unmasked: this is the holder's own document about their own money, and the
   * identity number is part of what makes it one.
   */
  holderIdNumber: string
  shares: number
  /** Pålydende per share. Null when the company details do not say. */
  parValue: number | null
  /** Tegningskurs — what this holder paid per share. */
  pricePerShare: number | null
  /** Overkurs per share: tegningskurs less pålydende. Null if either is unknown. */
  premium: number | null
  /** Tegningskurs × antall aksjer. Null when the price is unknown. */
  total: number | null
  /** ISO. The day the money arrived. */
  paidDate: string
  /** What the shares were issued under, named. */
  roundName: string
  /** ISO. The round's own date, for the reference line. */
  roundDate: string
  resolution: string
  note: string
  /** The required facts nothing on file could answer. Printed, never invented. */
  missing: MissingField[]
}

/**
 * Turn one payment into the document.
 *
 * Pure, and deliberately unaware of Firestore: the admin screen and the
 * shareholder's own screen read the same three inputs from different places,
 * and must not be able to produce two different receipts for one payment.
 */
export function buildReceipt(
  /**
   * Every field optional, for the same reason `paidTransactions` takes optional
   * arrays: the shareholder screen hands this a raw Firestore document, and a
   * holder with no address on file has no `address` key to trim.
   */
  holder: Partial<Pick<Shareholder, 'kind' | 'name' | 'address' | 'personnummer' | 'orgNumber'>>,
  tx: PaidTransaction,
  company: CompanyDetails,
  issuance: Issuance | undefined,
): Receipt {
  const text = (v: string | undefined) => (v ?? '').trim()
  const isCompany = holder.kind === 'company'
  const idNumber = text(isCompany ? holder.orgNumber : holder.personnummer)
  const parValue = company.parValue
  const price = tx.pricePerShare

  const missing: MissingField[] = [
    !text(company.legalName) && 'companyName',
    !text(company.orgNumber) && 'companyOrgNumber',
    parValue == null && 'parValue',
    !text(holder.address) && 'holderAddress',
    !idNumber && 'holderId',
    price == null && 'price',
    !tx.paidDate && 'paidDate',
  ].filter((v): v is MissingField => Boolean(v))

  return {
    kind: tx.kind,
    companyName: text(company.legalName),
    companyOrgNumber: text(company.orgNumber),
    holderName: holder.name ?? '',
    holderAddress: text(holder.address),
    holderIsCompany: isCompany,
    holderIdNumber: idNumber,
    shares: tx.shares,
    parValue,
    pricePerShare: price,
    // Overkurs is a subtraction, so it needs both halves. A negative one is
    // left as it computes rather than clamped: a price under par is a fact
    // about the round somebody should see, not a rounding error to hide.
    premium: price != null && parValue != null ? price - parValue : null,
    total: price != null ? price * tx.shares : null,
    paidDate: tx.paidDate,
    roundName: issuance?.name ?? '',
    roundDate: issuance?.date ?? '',
    resolution: tx.resolution,
    note: tx.note,
    missing,
  }
}


/**
 * What the holder is registered with in total, and as at when.
 *
 * The certificate carries this beside the round's own share count so that a
 * bevis for 500 shares can never read as though 500 is everything they own —
 * the reason Bjarte chose this shape on 2 Sep over a bare per-round figure.
 *
 * `asOf` is not decoration. The admin screen computes the total from the live
 * register and passes today; the shareholder's own screen reads it from the
 * generated cap table, which is as old as the last run of `buildCapTable.ts`.
 * A total on a certificate has to say which day it is true for, and printing
 * the projection's own `builtAt` is the difference between a dated statement
 * and a stale one pretending to be current.
 *
 * `shares` may be null — a holder the cap table has not been rebuilt for yet.
 * The certificate then states the round and leaves the total out rather than
 * printing a zero somebody would read as "owns nothing".
 */
export interface RegisteredHolding {
  shares: number | null
  /** ISO date the total is true as at. */
  asOf: string
}

/**
 * Shares this holder owns that Foretaksregisteret has been told about.
 *
 * **The only figure an aksjeeierbevis may state as a holding** — 17 Sep 2026.
 * The certificate is a document of title, and a title to shares that are not yet
 * on the public register is a claim about something the state does not yet
 * believe exists. Emisjon 4 is the case that made this urgent: 3 268 shares
 * issued, paid for and correct in our own register, and not reported — a
 * certificate counting them would have told thirty-eight holders they own more
 * of the company than the register of companies says anybody does.
 *
 * So a round counts only once `registeredAt` is set on it. The same field
 * already gates *which* acquisition may have a certificate at all; this is the
 * same rule applied to the total beside it, which was reading the cap table and
 * counting every round whether reported or not.
 *
 * ## Settled transfers count, and the reason they have to
 *
 * Shares that changed hands were issued in some round before they moved, so a
 * transfer is a movement inside the register rather than a new claim on it. The
 * rows this receives do not say which round the shares originally came from —
 * the per-holder transfer record names a counterparty and a count, deliberately,
 * so that reading it does not mean reading somebody else's holding.
 *
 * The residual case, stated rather than discovered: shares issued in an
 * unreported round and then transferred on would be counted here through the
 * buyer. It cannot arise today — nothing transfers shares from a round that has
 * not closed — and closing it properly means the transfer record carrying the
 * provenance, which is a change to `buildCapTable.ts` and not to this function.
 *
 * Pending transfers move nothing, here as everywhere else in the register.
 */
export function registeredShares(
  holder: Pick<Shareholder, 'purchases' | 'options'>,
  issuances: Pick<Issuance, 'id' | 'registeredAt'>[],
  moves: { shares: number; direction: 'in' | 'out'; settled: boolean }[] = [],
): number {
  const reported = new Set(
    issuances.filter((i) => (i.registeredAt ?? '').trim() !== '').map((i) => i.id),
  )

  const issued =
    holder.purchases.reduce(
      (n, p) => n + (reported.has(p.issuanceId) ? p.shares || 0 : 0),
      0,
    ) +
    holder.options.reduce(
      (n, o) =>
        n +
        (o.status === 'exercised' && o.paidDate && reported.has(o.exerciseIssuanceId)
          ? o.count || 0
          : 0),
      0,
    )

  const moved = moves.reduce(
    (n, m) => (m.settled ? n + (m.direction === 'in' ? m.shares || 0 : -(m.shares || 0)) : n),
    0,
  )

  // A holder who has sold more registered shares than they were issued is a
  // register that disagrees with itself; the certificate says nothing rather
  // than a negative number.
  return Math.max(0, issued + moved)
}

/** The aksjeeierbevis, resolved to what a view has to print. */
export interface Certificate {
  companyName: string
  companyOrgNumber: string
  holderName: string
  holderAddress: string
  holderIsCompany: boolean
  holderIdNumber: string
  /** Shares acquired in this round. */
  shares: number
  roundName: string
  /** ISO. The round's own date. */
  roundDate: string
  /** ISO. The day the increase was reported to Foretaksregisteret. */
  registeredAt: string
  total: RegisteredHolding
  missing: MissingField[]
}

/**
 * Turn one acquisition into a certificate.
 *
 * Deliberately has no opinion about money: a price and a payment date appear
 * nowhere on it. An aksjeeierbevis says *these shares are yours*, and what they
 * cost is the receipt's business — putting a kurs on a certificate of title
 * invites it to be used as the tax document, which it is not.
 */
export function buildCertificate(
  holder: Partial<Pick<Shareholder, 'kind' | 'name' | 'address' | 'personnummer' | 'orgNumber'>>,
  tx: PaidTransaction,
  company: CompanyDetails,
  issuance: Issuance | undefined,
  total: RegisteredHolding,
): Certificate {
  const text = (v: string | undefined) => (v ?? '').trim()
  const isCompany = holder.kind === 'company'
  const idNumber = text(isCompany ? holder.orgNumber : holder.personnummer)
  const registeredAt = issuance?.registeredAt ?? ''

  const missing: MissingField[] = [
    !text(company.legalName) && 'companyName',
    !text(company.orgNumber) && 'companyOrgNumber',
    !text(holder.address) && 'holderAddress',
    !idNumber && 'holderId',
    // Only reachable if a caller ignores `certifiableTransactions`, which is
    // exactly when a document must not quietly look complete. Payment is not
    // listed: an unpaid acquisition has no certificate at all, so there is no
    // document here to print a gap on.
    !registeredAt && 'registered',
  ].filter((v): v is MissingField => Boolean(v))

  return {
    companyName: text(company.legalName),
    companyOrgNumber: text(company.orgNumber),
    holderName: holder.name ?? '',
    holderAddress: text(holder.address),
    holderIsCompany: isCompany,
    holderIdNumber: idNumber,
    shares: tx.shares,
    roundName: issuance?.name ?? '',
    roundDate: issuance?.date ?? '',
    registeredAt,
    total,
    missing,
  }
}
